import { fnv1a64 } from "../../core/determinism/canonical";
import { assertAuthoritativeInteger } from "../../core/determinism/types";
import type { LeaseBook } from "./leases";

export const OWNER_KIND = Object.freeze({
  FIELD: 1,
  ACTOR_CARGO: 2,
  PEN_STAGING: 3,
  PEN_OUTPUT: 4,
  MARKET_SERVICE: 5,
  RECOVERY_PILE: 6,
} as const);

export const ACTOR_KIND = Object.freeze({ HUMANOID: 1 } as const);

export const EVENT_KIND = Object.freeze({
  TRANSFER_COMMITTED: 1,
  TRANSFER_REJECTED: 2,
  ITEM_CREATED: 3,
  RECOVERY_COMMITTED: 4,
} as const);

export type OwnerKind = (typeof OWNER_KIND)[keyof typeof OWNER_KIND];
export type EventKind = (typeof EVENT_KIND)[keyof typeof EVENT_KIND];

export interface OwnerRef {
  readonly ownerKind: OwnerKind;
  readonly ownerId: number;
  readonly version: number;
}

export interface OwnerState extends OwnerRef {
  readonly capacity: number;
  readonly acceptedItemKinds: readonly number[];
  readonly navRelevant: boolean;
}

export interface ItemInstance {
  readonly itemId: number;
  readonly kind: number;
  readonly lineageId: number;
  readonly ownerKind: OwnerKind;
  readonly ownerId: number;
  readonly slotIndex: number;
  readonly version: number;
}

export interface ActorAuthority {
  readonly actorId: number;
  readonly actorKind: typeof ACTOR_KIND.HUMANOID;
  readonly actionId: number;
  readonly enabled: boolean;
  readonly present: boolean;
}

export interface Receipt {
  readonly sequence: number;
  readonly idempotencyKey: number;
}

export interface LedgerEvent {
  readonly sequence: number;
  readonly eventKind: EventKind;
  readonly reason: string;
  readonly actorId: number;
  readonly actionId: number;
  readonly idempotencyKey: number;
  readonly itemIds: readonly number[];
}

export interface LedgerReplayEntry {
  readonly operation: "transfer" | "create" | "actor-removal" | "interruption";
  readonly tick: number;
  readonly command: TransferCommand | CreateCommand | RecoveryCommand | ActorRemovalCommand;
  readonly leases: LeaseBook | null;
}

export interface ActorRemovalCommand {
  readonly actorId: number;
  readonly destination: OwnerRef;
}

export interface LedgerState {
  readonly items: readonly Readonly<ItemInstance>[];
  readonly owners: readonly Readonly<OwnerState>[];
  readonly actors: readonly Readonly<ActorAuthority>[];
  readonly receipts: readonly Readonly<Receipt>[];
  readonly events: readonly Readonly<LedgerEvent>[];
  readonly currency: number;
  readonly navRevision: number;
  readonly nextEventSequence: number;
}

export interface LedgerStateInput {
  readonly items: readonly ItemInstance[];
  readonly owners: readonly OwnerState[];
  readonly actors: readonly ActorAuthority[];
  readonly receipts?: readonly Receipt[];
  readonly events?: readonly LedgerEvent[];
  readonly currency: number;
  readonly navRevision: number;
  readonly nextEventSequence?: number;
}

export interface TransferCommand {
  readonly actorId: number;
  readonly actionId: number;
  readonly itemIds: readonly number[];
  readonly source: OwnerRef;
  readonly destination: OwnerRef;
  readonly expectedItemKind: number;
  readonly capacityClaim: number;
  readonly navRevision: number | null;
  readonly reservationLeaseId: number | null;
  readonly idempotencyKey: number;
  readonly cancelled: boolean;
}

export interface CreateCommand {
  readonly actorId: number;
  readonly actionId: number;
  readonly itemId: number;
  readonly kind: number;
  readonly lineageId: number;
  readonly destination: OwnerRef;
  readonly capacityClaim: number;
  readonly navRevision: number | null;
  readonly reservationLeaseId: number | null;
  readonly idempotencyKey: number;
  readonly cancelled: boolean;
}

export interface RecoveryCommand {
  readonly actorId: number;
  readonly actionId: number;
  readonly itemIds: readonly number[];
  readonly destination: OwnerRef;
}

export interface CommitResult {
  readonly committed: boolean;
  readonly reason: string;
  readonly state: LedgerState;
}

const OWNER_KINDS = new Set<number>(Object.values(OWNER_KIND));

function integer(value: unknown, label: string, minimum = 0): number {
  return assertAuthoritativeInteger(value, label, minimum);
}

function ownerKey(owner: Pick<OwnerRef, "ownerKind" | "ownerId">): string {
  return `${owner.ownerKind}:${owner.ownerId}`;
}

function compareOwner(left: Pick<OwnerRef, "ownerKind" | "ownerId">, right: Pick<OwnerRef, "ownerKind" | "ownerId">): number {
  return left.ownerKind - right.ownerKind || left.ownerId - right.ownerId;
}

function assertOwnerKind(value: unknown): OwnerKind {
  const kind = integer(value, "ownerKind", 1);
  if (!OWNER_KINDS.has(kind)) {
    throw new RangeError("ownerKind is not stable");
  }
  return kind as OwnerKind;
}

function cloneOwnerRef(value: OwnerRef): OwnerRef {
  return Object.freeze({
    ownerKind: assertOwnerKind(value.ownerKind),
    ownerId: integer(value.ownerId, "ownerId", 1),
    version: integer(value.version, "owner version"),
  });
}

function freezeState(state: LedgerState): LedgerState {
  const items = Object.isFrozen(state.items)
    ? state.items
    : Object.freeze(state.items.map((item) => Object.isFrozen(item) ? item : Object.freeze({ ...item })));
  const owners = Object.isFrozen(state.owners)
    ? state.owners
    : Object.freeze(state.owners.map((owner) => Object.isFrozen(owner) ? owner : Object.freeze({ ...owner, acceptedItemKinds: Object.freeze([...owner.acceptedItemKinds]) })));
  const actors = Object.isFrozen(state.actors)
    ? state.actors
    : Object.freeze(state.actors.map((actor) => Object.isFrozen(actor) ? actor : Object.freeze({ ...actor })));
  const receipts = Object.isFrozen(state.receipts)
    ? state.receipts
    : Object.freeze(state.receipts.map((receipt) => Object.freeze({ ...receipt })));
  const events = Object.isFrozen(state.events)
    ? state.events
    : Object.freeze(state.events.map((event) => Object.freeze({ ...event, itemIds: Object.freeze([...event.itemIds]) })));
  return Object.freeze({
    items,
    owners,
    actors,
    receipts,
    events,
    currency: state.currency,
    navRevision: state.navRevision,
    nextEventSequence: state.nextEventSequence,
  });
}

export function createLedgerState(input: LedgerStateInput): LedgerState {
  const owners = input.owners.map((owner): OwnerState => {
    const ref = cloneOwnerRef(owner);
    const acceptedItemKinds = [...owner.acceptedItemKinds].map((kind) => integer(kind, "accepted item kind", 1)).sort((a, b) => a - b);
    if (new Set(acceptedItemKinds).size !== acceptedItemKinds.length) {
      throw new RangeError("duplicate accepted item kind");
    }
    return { ...ref, capacity: integer(owner.capacity, "owner capacity"), acceptedItemKinds, navRelevant: Boolean(owner.navRelevant) };
  }).sort(compareOwner);
  const ownerKeys = owners.map(ownerKey);
  if (new Set(ownerKeys).size !== ownerKeys.length) {
    throw new RangeError("duplicate owner");
  }
  const knownOwners = new Set(ownerKeys);

  const items = input.items.map((item): ItemInstance => ({
    itemId: integer(item.itemId, "itemId", 1),
    kind: integer(item.kind, "item kind", 1),
    lineageId: integer(item.lineageId, "lineageId", 1),
    ownerKind: assertOwnerKind(item.ownerKind),
    ownerId: integer(item.ownerId, "item ownerId", 1),
    slotIndex: integer(item.slotIndex, "slotIndex"),
    version: integer(item.version, "item version"),
  })).sort((a, b) => a.itemId - b.itemId);
  if (new Set(items.map((item) => item.itemId)).size !== items.length) {
    throw new RangeError("duplicate itemId");
  }
  for (const item of items) {
    if (!knownOwners.has(ownerKey(item))) {
      throw new RangeError("unknown item owner");
    }
  }
  for (const owner of owners) {
    const owned = items.filter((item) => ownerKey(item) === ownerKey(owner));
    if (owned.length > owner.capacity) {
      throw new RangeError("owner capacity exceeded");
    }
    if (owned.some((item) => !owner.acceptedItemKinds.includes(item.kind))) {
      throw new RangeError("owner rejects item kind");
    }
    if (new Set(owned.map((item) => item.slotIndex)).size !== owned.length) {
      throw new RangeError("duplicate owner slot");
    }
  }

  const actors = input.actors.map((actor): ActorAuthority => {
    if (actor.actorKind !== ACTOR_KIND.HUMANOID) {
      throw new RangeError("actorKind must be HumanoidActor");
    }
    return {
      actorId: integer(actor.actorId, "actorId", 1),
      actorKind: ACTOR_KIND.HUMANOID,
      actionId: integer(actor.actionId, "actor actionId"),
      enabled: Boolean(actor.enabled),
      present: Boolean(actor.present),
    };
  }).sort((a, b) => a.actorId - b.actorId);
  if (new Set(actors.map((actor) => actor.actorId)).size !== actors.length) {
    throw new RangeError("duplicate actorId");
  }

  const receipts = [...(input.receipts ?? [])].map((receipt) => ({
    sequence: integer(receipt.sequence, "receipt sequence"),
    idempotencyKey: integer(receipt.idempotencyKey, "receipt idempotencyKey", 1),
  })).sort((a, b) => a.sequence - b.sequence);
  if (new Set(receipts.map((receipt) => receipt.sequence)).size !== receipts.length || new Set(receipts.map((receipt) => receipt.idempotencyKey)).size !== receipts.length) {
    throw new RangeError("duplicate receipt");
  }
  const events = [...(input.events ?? [])].map((event) => ({ ...event, itemIds: [...event.itemIds] })).sort((a, b) => a.sequence - b.sequence);
  for (let index = 0; index < events.length; index += 1) {
    const event = events[index]!;
    integer(event.sequence, "event sequence");
    integer(event.eventKind, "event kind", 1);
    integer(event.actorId, "event actorId");
    integer(event.actionId, "event actionId");
    integer(event.idempotencyKey, "event idempotencyKey");
    for (const itemId of event.itemIds) integer(itemId, "event itemId", 1);
    if (index > 0 && event.sequence !== events[index - 1]!.sequence + 1) {
      throw new RangeError("event sequence is not contiguous");
    }
  }
  const nextEventSequence = input.nextEventSequence ?? (events.length === 0 ? 0 : events.at(-1)!.sequence + 1);
  if (nextEventSequence !== (events.length === 0 ? 0 : events.at(-1)!.sequence + 1)) {
    throw new RangeError("nextEventSequence does not follow events");
  }

  return freezeState({
    items,
    owners,
    actors,
    receipts,
    events,
    currency: integer(input.currency, "currency"),
    navRevision: integer(input.navRevision, "navRevision"),
    nextEventSequence: integer(nextEventSequence, "nextEventSequence"),
  });
}

function findOwner(state: LedgerState, ref: Pick<OwnerRef, "ownerKind" | "ownerId">): OwnerState | undefined {
  return state.owners.find((owner) => owner.ownerKind === ref.ownerKind && owner.ownerId === ref.ownerId);
}

function validateActor(state: LedgerState, actorId: number, actionId: number): string | null {
  const actor = state.actors.find((candidate) => candidate.actorId === actorId);
  if (actor === undefined) return "actor-missing";
  if (!actor.enabled) return "actor-disabled";
  if (!actor.present) return "actor-not-present";
  if (actor.actorKind !== ACTOR_KIND.HUMANOID) return "actor-not-humanoid";
  if (actor.actionId !== actionId) return "action-mismatch";
  return null;
}

function occupiedCount(state: LedgerState, owner: Pick<OwnerRef, "ownerKind" | "ownerId">): number {
  return state.items.filter((item) => item.ownerKind === owner.ownerKind && item.ownerId === owner.ownerId).length;
}

function validateClaim(state: LedgerState, book: LeaseBook | null, leaseId: number | null, actorId: number, actionId: number, destination: OwnerState, capacity: number, tick: number): string | null {
  const active = book?.leases.filter((lease) => lease.expiresTick > tick && lease.ownerKind === destination.ownerKind && lease.ownerId === destination.ownerId) ?? [];
  if (leaseId === null) {
    const reserved = active.reduce((sum, lease) => sum + lease.capacity, 0);
    return occupiedCount(state, destination) + reserved + capacity > destination.capacity ? "conflicting-claim" : null;
  }
  const lease = book?.leases.find((candidate) => candidate.leaseId === leaseId);
  if (lease === undefined) return "claim-missing";
  if (lease.expiresTick <= tick) return "expired-claim";
  if (lease.actorId !== actorId || lease.actionId !== actionId || lease.ownerKind !== destination.ownerKind || lease.ownerId !== destination.ownerId || lease.capacity !== capacity) return "conflicting-claim";
  if (lease.ownerVersion !== destination.version) return "stale-destination-version";
  const otherReserved = active.filter((candidate) => candidate.leaseId !== leaseId).reduce((sum, candidate) => sum + candidate.capacity, 0);
  if (occupiedCount(state, destination) + otherReserved + capacity > destination.capacity) return "conflicting-claim";
  return null;
}

function nextVersion(value: number): number | null {
  return value === Number.MAX_SAFE_INTEGER ? null : value + 1;
}

function appendEvent(state: LedgerState, eventKind: EventKind, reason: string, actorId: number, actionId: number, idempotencyKey: number, itemIds: readonly number[], receipt: boolean): LedgerState {
  const sequence = state.nextEventSequence;
  if (sequence === Number.MAX_SAFE_INTEGER) {
    throw new RangeError("event sequence is exhausted");
  }
  const event = Object.freeze({ sequence, eventKind, reason, actorId, actionId, idempotencyKey, itemIds: Object.freeze([...itemIds].sort((a, b) => a - b)) });
  const nextReceipt = Object.freeze({ sequence, idempotencyKey });
  return freezeState({
    ...state,
    events: Object.freeze([...state.events, event]),
    receipts: receipt ? Object.freeze([...state.receipts, nextReceipt]) : state.receipts,
    nextEventSequence: sequence + 1,
  });
}

function reject(state: LedgerState, reason: string, actorId: number, actionId: number, idempotencyKey: number, itemIds: readonly number[]): CommitResult {
  return Object.freeze({ committed: false, reason, state: appendEvent(state, EVENT_KIND.TRANSFER_REJECTED, reason, actorId, actionId, idempotencyKey, itemIds, false) });
}

function safeTransferCommand(command: TransferCommand): string | null {
  try {
    integer(command.actorId, "actorId", 1); integer(command.actionId, "actionId"); integer(command.expectedItemKind, "expectedItemKind", 1);
    integer(command.capacityClaim, "capacityClaim", 1); integer(command.idempotencyKey, "idempotencyKey", 1);
    cloneOwnerRef(command.source); cloneOwnerRef(command.destination);
    if (command.navRevision !== null) integer(command.navRevision, "navRevision");
    if (command.reservationLeaseId !== null) integer(command.reservationLeaseId, "reservationLeaseId", 1);
    for (const itemId of command.itemIds) integer(itemId, "itemId", 1);
    return null;
  } catch {
    return "invalid-command";
  }
}

export function commitTransfer(state: LedgerState, command: TransferCommand, leases: LeaseBook | null, tick: number): CommitResult {
  const safeActor = Number.isSafeInteger(command.actorId) ? command.actorId : 0;
  const safeAction = Number.isSafeInteger(command.actionId) ? command.actionId : 0;
  const safeKey = Number.isSafeInteger(command.idempotencyKey) ? command.idempotencyKey : 0;
  const invalid = safeTransferCommand(command);
  if (invalid !== null) return reject(state, invalid, safeActor, safeAction, safeKey, []);
  const itemIds = [...command.itemIds].sort((a, b) => a - b);
  if (command.cancelled) return reject(state, "cancelled", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  const actorReason = validateActor(state, command.actorId, command.actionId);
  if (actorReason !== null) return reject(state, actorReason, command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (state.receipts.some((receipt) => receipt.idempotencyKey === command.idempotencyKey)) return reject(state, "duplicate-idempotency-key", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (itemIds.length === 0 || new Set(itemIds).size !== itemIds.length) return reject(state, "duplicate-item-id", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (ownerKey(command.source) === ownerKey(command.destination)) return reject(state, "same-owner", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  const source = findOwner(state, command.source);
  const destination = findOwner(state, command.destination);
  if (source === undefined || destination === undefined) return reject(state, "owner-missing", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if ((source.ownerKind === OWNER_KIND.ACTOR_CARGO && source.ownerId !== command.actorId) || (destination.ownerKind === OWNER_KIND.ACTOR_CARGO && destination.ownerId !== command.actorId)) return reject(state, "actor-cargo-mismatch", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (source.version !== command.source.version) return reject(state, "stale-source-version", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (destination.version !== command.destination.version) return reject(state, "stale-destination-version", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  const selected = itemIds.map((itemId) => state.items.find((item) => item.itemId === itemId));
  if (selected.some((item) => item === undefined)) return reject(state, "item-missing", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (selected.some((item) => item!.ownerKind !== source.ownerKind || item!.ownerId !== source.ownerId)) return reject(state, "source-mismatch", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (selected.some((item) => item!.kind !== command.expectedItemKind) || !destination.acceptedItemKinds.includes(command.expectedItemKind)) return reject(state, "wrong-item-kind", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (command.capacityClaim !== itemIds.length) return reject(state, "capacity-claim-mismatch", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (occupiedCount(state, destination) + itemIds.length > destination.capacity) return reject(state, "insufficient-capacity", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (destination.navRelevant && command.navRevision === null) return reject(state, "nav-revision-required", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (command.navRevision !== null && command.navRevision !== state.navRevision) return reject(state, "nav-revision-mismatch", command.actorId, command.actionId, command.idempotencyKey, itemIds);
  const claimReason = validateClaim(state, leases, command.reservationLeaseId, command.actorId, command.actionId, destination, command.capacityClaim, tick);
  if (claimReason !== null) return reject(state, claimReason, command.actorId, command.actionId, command.idempotencyKey, itemIds);
  if (nextVersion(source.version) === null || nextVersion(destination.version) === null || selected.some((item) => nextVersion(item!.version) === null)) return reject(state, "version-exhausted", command.actorId, command.actionId, command.idempotencyKey, itemIds);

  const selectedIds = new Set(itemIds);
  const occupiedSlots = new Set(state.items.filter((item) => item.ownerKind === destination.ownerKind && item.ownerId === destination.ownerId).map((item) => item.slotIndex));
  const slots: number[] = [];
  for (let slot = 0; slots.length < itemIds.length; slot += 1) if (!occupiedSlots.has(slot)) slots.push(slot);
  const slotById = new Map(itemIds.map((itemId, index) => [itemId, slots[index]!]));
  const next = freezeState({
    ...state,
    items: state.items.map((item) => selectedIds.has(item.itemId) ? { ...item, ownerKind: destination.ownerKind, ownerId: destination.ownerId, slotIndex: slotById.get(item.itemId)!, version: nextVersion(item.version)! } : item),
    owners: state.owners.map((owner) => ownerKey(owner) === ownerKey(source) || ownerKey(owner) === ownerKey(destination) ? { ...owner, version: nextVersion(owner.version)! } : owner),
  });
  return Object.freeze({ committed: true, reason: "committed", state: appendEvent(next, EVENT_KIND.TRANSFER_COMMITTED, "committed", command.actorId, command.actionId, command.idempotencyKey, itemIds, true) });
}

function safeCreateCommand(command: CreateCommand): string | null {
  try {
    integer(command.actorId, "actorId", 1); integer(command.actionId, "actionId"); integer(command.itemId, "itemId", 1); integer(command.kind, "kind", 1); integer(command.lineageId, "lineageId", 1); integer(command.capacityClaim, "capacityClaim", 1); integer(command.idempotencyKey, "idempotencyKey", 1); cloneOwnerRef(command.destination);
    if (command.navRevision !== null) integer(command.navRevision, "navRevision");
    if (command.reservationLeaseId !== null) integer(command.reservationLeaseId, "reservationLeaseId", 1);
    return null;
  } catch { return "invalid-command"; }
}

export function commitCreate(state: LedgerState, command: CreateCommand, leases: LeaseBook | null, tick: number): CommitResult {
  const invalid = safeCreateCommand(command);
  if (invalid !== null) return reject(state, invalid, Number.isSafeInteger(command.actorId) ? command.actorId : 0, Number.isSafeInteger(command.actionId) ? command.actionId : 0, Number.isSafeInteger(command.idempotencyKey) ? command.idempotencyKey : 0, []);
  if (command.cancelled) return reject(state, "cancelled", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  const actorReason = validateActor(state, command.actorId, command.actionId);
  if (actorReason !== null) return reject(state, actorReason, command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (state.receipts.some((receipt) => receipt.idempotencyKey === command.idempotencyKey)) return reject(state, "duplicate-idempotency-key", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (state.items.some((item) => item.itemId === command.itemId)) return reject(state, "duplicate-item-id", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  const destination = findOwner(state, command.destination);
  if (destination === undefined) return reject(state, "owner-missing", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (destination.ownerKind === OWNER_KIND.ACTOR_CARGO && destination.ownerId !== command.actorId) return reject(state, "actor-cargo-mismatch", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (destination.version !== command.destination.version) return reject(state, "stale-destination-version", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (!destination.acceptedItemKinds.includes(command.kind)) return reject(state, "wrong-item-kind", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (command.capacityClaim !== 1) return reject(state, "capacity-claim-mismatch", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (occupiedCount(state, destination) + 1 > destination.capacity) return reject(state, "insufficient-capacity", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (destination.navRelevant && command.navRevision === null) return reject(state, "nav-revision-required", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (command.navRevision !== null && command.navRevision !== state.navRevision) return reject(state, "nav-revision-mismatch", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  const claimReason = validateClaim(state, leases, command.reservationLeaseId, command.actorId, command.actionId, destination, 1, tick);
  if (claimReason !== null) return reject(state, claimReason, command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  if (nextVersion(destination.version) === null) return reject(state, "version-exhausted", command.actorId, command.actionId, command.idempotencyKey, [command.itemId]);
  const used = new Set(state.items.filter((item) => item.ownerKind === destination.ownerKind && item.ownerId === destination.ownerId).map((item) => item.slotIndex));
  let slotIndex = 0; while (used.has(slotIndex)) slotIndex += 1;
  const next = freezeState({ ...state, items: [...state.items, { itemId: command.itemId, kind: command.kind, lineageId: command.lineageId, ownerKind: destination.ownerKind, ownerId: destination.ownerId, slotIndex, version: 0 }].sort((a, b) => a.itemId - b.itemId), owners: state.owners.map((owner) => ownerKey(owner) === ownerKey(destination) ? { ...owner, version: nextVersion(owner.version)! } : owner) });
  return Object.freeze({ committed: true, reason: "committed", state: appendEvent(next, EVENT_KIND.ITEM_CREATED, "committed", command.actorId, command.actionId, command.idempotencyKey, [command.itemId], true) });
}

function recoveryMove(state: LedgerState, itemIds: readonly number[], destinationRef: OwnerRef, actorId: number, actionId: number): CommitResult {
  const destination = findOwner(state, destinationRef);
  if (destination === undefined || destination.ownerKind !== OWNER_KIND.RECOVERY_PILE) return reject(state, "invalid-recovery-destination", actorId, actionId, 0, itemIds);
  if (destination.version !== destinationRef.version) return reject(state, "stale-destination-version", actorId, actionId, 0, itemIds);
  const selected = itemIds.map((id) => state.items.find((item) => item.itemId === id));
  if (occupiedCount(state, destination) + itemIds.length > destination.capacity) return reject(state, "insufficient-capacity", actorId, actionId, 0, itemIds);
  if (selected.some((item) => !destination.acceptedItemKinds.includes(item!.kind))) return reject(state, "wrong-item-kind", actorId, actionId, 0, itemIds);
  const sourceOwners = selected.map((item) => findOwner(state, item!));
  if (nextVersion(destination.version) === null || selected.some((item) => nextVersion(item!.version) === null) || sourceOwners.some((owner) => owner === undefined || nextVersion(owner.version) === null)) return reject(state, "version-exhausted", actorId, actionId, 0, itemIds);
  const ids = [...itemIds].sort((a, b) => a - b); const idSet = new Set(ids);
  const used = new Set(state.items.filter((item) => item.ownerKind === destination.ownerKind && item.ownerId === destination.ownerId).map((item) => item.slotIndex));
  const slots = new Map<number, number>(); let slot = 0;
  for (const id of ids) { while (used.has(slot)) slot += 1; slots.set(id, slot); used.add(slot); }
  const sourceKeys = new Set(selected.map((item) => ownerKey(item!)));
  const next = freezeState({ ...state, items: state.items.map((item) => idSet.has(item.itemId) ? { ...item, ownerKind: destination.ownerKind, ownerId: destination.ownerId, slotIndex: slots.get(item.itemId)!, version: nextVersion(item.version)! } : item), owners: state.owners.map((owner) => sourceKeys.has(ownerKey(owner)) || ownerKey(owner) === ownerKey(destination) ? { ...owner, version: nextVersion(owner.version)! } : owner) });
  return Object.freeze({ committed: true, reason: "recovered", state: appendEvent(next, EVENT_KIND.RECOVERY_COMMITTED, "recovered", actorId, actionId, 0, ids, false) });
}

export function recoverActorRemoval(state: LedgerState, actorId: number, destination: OwnerRef): CommitResult {
  const actor = state.actors.find((candidate) => candidate.actorId === actorId);
  if (actor === undefined) return reject(state, "actor-missing", actorId, 0, 0, []);
  if (findOwner(state, destination)?.ownerKind !== OWNER_KIND.RECOVERY_PILE) return reject(state, "invalid-recovery-destination", actorId, actor.actionId, 0, []);
  const cargoIds = state.items.filter((item) => item.ownerKind === OWNER_KIND.ACTOR_CARGO && item.ownerId === actorId).map((item) => item.itemId);
  const removeActor = (source: LedgerState): LedgerState => freezeState({ ...source, actors: source.actors.map((entry) => entry.actorId === actorId ? { ...entry, enabled: false, present: false, actionId: 0 } : entry) });
  const base = removeActor(state);
  if (cargoIds.length === 0) return Object.freeze({ committed: true, reason: "recovered", state: appendEvent(base, EVENT_KIND.RECOVERY_COMMITTED, "recovered", actorId, actor.actionId, 0, [], false) });
  const moved = recoveryMove(state, cargoIds, destination, actorId, actor.actionId);
  if (!moved.committed) return moved;
  return Object.freeze({ ...moved, state: removeActor(moved.state) });
}

export function recoverInterruptedItems(state: LedgerState, command: RecoveryCommand): CommitResult {
  const actorReason = validateActor(state, command.actorId, command.actionId);
  if (actorReason !== null) return reject(state, actorReason, command.actorId, command.actionId, 0, command.itemIds);
  const selected = command.itemIds.map((id) => state.items.find((item) => item.itemId === id));
  if (new Set(command.itemIds).size !== command.itemIds.length) return reject(state, "duplicate-item-id", command.actorId, command.actionId, 0, command.itemIds);
  if (selected.some((item) => item === undefined)) return reject(state, "item-missing", command.actorId, command.actionId, 0, command.itemIds);
  if (selected.some((item) => !([OWNER_KIND.ACTOR_CARGO, OWNER_KIND.PEN_STAGING, OWNER_KIND.PEN_OUTPUT] as number[]).includes(item!.ownerKind))) return reject(state, "invalid-recovery-source", command.actorId, command.actionId, 0, command.itemIds);
  if (selected.some((item) => item!.ownerKind === OWNER_KIND.ACTOR_CARGO && item!.ownerId !== command.actorId)) return reject(state, "invalid-recovery-source", command.actorId, command.actionId, 0, command.itemIds);
  return recoveryMove(state, command.itemIds, command.destination, command.actorId, command.actionId);
}

export function hashLedgerLog(state: Pick<LedgerState, "events">): string {
  const canonical = JSON.stringify(state.events.map((event) => [event.sequence, event.eventKind, event.reason, event.actorId, event.actionId, event.idempotencyKey, [...event.itemIds].sort((a, b) => a - b)]));
  return fnv1a64(new TextEncoder().encode(canonical));
}

export function replayLedger(initial: LedgerState, log: readonly LedgerReplayEntry[]): LedgerState {
  let state = initial;
  for (const entry of log) {
    switch (entry.operation) {
      case "transfer": state = commitTransfer(state, entry.command as TransferCommand, entry.leases, entry.tick).state; break;
      case "create": state = commitCreate(state, entry.command as CreateCommand, entry.leases, entry.tick).state; break;
      case "actor-removal": {
        const command = entry.command as ActorRemovalCommand;
        state = recoverActorRemoval(state, command.actorId, command.destination).state;
        break;
      }
      case "interruption": state = recoverInterruptedItems(state, entry.command as RecoveryCommand).state; break;
    }
  }
  return state;
}
